import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const DEFAULT_FILTERS = {
  // Phase 9.8 — Default preset changed from "month" to "last_month" by client
  // request. Rationale: the current calendar month usually has zero
  // time_entries (uploaded once per month) so opening the app with tx/hr
  // charts filled with nulls is confusing. Last month always has both
  // transactions AND hours.
  preset: "last_month",
  date_from: null,
  date_to: null,
  source_report: "GENDETAILED_PROD",
  transaction_types: [],
  department_ids: [],
  employee_ids: [],
  // Phase 9.4 — day-of-week drill-down. Emitted by the heatmap cell click
  // (0..6 Mon=0). Empty = no restriction.
  day_of_week: [],
};

function fromQs(sp) {
  const get = (k) => sp.get(k);
  const arr = (k) => {
    const v = sp.get(k);
    return v ? v.split(",").filter(Boolean) : [];
  };
  const arrInt = (k) => {
    const v = sp.get(k);
    if (!v) return [];
    return v
      .split(",")
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
  };
  return {
    preset: get("preset") || DEFAULT_FILTERS.preset,
    date_from: get("date_from"),
    date_to: get("date_to"),
    source_report: get("source_report") || DEFAULT_FILTERS.source_report,
    transaction_types: arr("transaction_types"),
    department_ids: arr("department_ids"),
    employee_ids: arr("employee_ids"),
    day_of_week: arrInt("day_of_week"),
  };
}

function toQs(filters) {
  const out = {};
  const f = filters || {};
  if (f.preset && f.preset !== DEFAULT_FILTERS.preset) out.preset = f.preset;
  if (f.date_from) out.date_from = f.date_from;
  if (f.date_to) out.date_to = f.date_to;
  if (f.source_report && f.source_report !== DEFAULT_FILTERS.source_report)
    out.source_report = f.source_report;
  if (f.transaction_types?.length)
    out.transaction_types = f.transaction_types.join(",");
  if (f.department_ids?.length)
    out.department_ids = f.department_ids.join(",");
  if (f.employee_ids?.length) out.employee_ids = f.employee_ids.join(",");
  if (f.day_of_week?.length) out.day_of_week = f.day_of_week.join(",");
  return out;
}

export function useFilters() {
  const [sp, setSp] = useSearchParams();
  const [filters, setFiltersState] = useState(() => fromQs(sp));

  // When the URL changes externally (back/forward, deep link) sync local state.
  useEffect(() => {
    setFiltersState(fromQs(sp));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp.toString()]);

  const setFilters = useCallback(
    (next) => {
      const merged = typeof next === "function" ? next(filters) : { ...filters, ...next };
      setFiltersState(merged);
      setSp(toQs(merged), { replace: true });
    },
    [filters, setSp]
  );

  const reset = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setSp({}, { replace: true });
  }, [setSp]);

  // Payload sent to backend (preset wins over date range unless custom).
  const payload = useMemo(
    () => ({
      preset: filters.preset,
      date_from: filters.preset === "custom" ? filters.date_from : null,
      date_to: filters.preset === "custom" ? filters.date_to : null,
      source_report: filters.source_report || null,
      transaction_types: filters.transaction_types || [],
      department_ids: filters.department_ids || [],
      employee_ids: filters.employee_ids || [],
      day_of_week: filters.day_of_week || [],
    }),
    [filters]
  );

  return { filters, setFilters, reset, payload };
}
